<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import type FlightSchool from '@/types/FlightSchool';
import { useSchoolStore } from '@/stores/school';
import FsumNavButton from '@/components/Navigation/FsumNavButton.vue';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import { type AvailableParts, availableParts } from '@/types/Certificate';
import { usePartStore } from '@/stores/part';

const props = defineProps<{
    currentSchool: FlightSchool|null,
}>();

const schoolFilter = ref('');
const schoolStore = useSchoolStore();
const schoolDialogOpen = ref(false);

const filteredSchools = computed<FlightSchool[]>(() => {
    if (schoolFilter.value === '') {
        return schoolStore.schools;
    }

    return schoolStore.schools.filter((school) => school.name.toLowerCase()
        .includes(schoolFilter.value.toLowerCase()));
});

const route = useRoute();
const partStore = usePartStore();

const currentPart = computed(() => {
    const partFromRoute = Number(route.params.part) as AvailableParts;
    const isPartValid = availableParts.includes(partFromRoute);

    if (route.params.part && isPartValid) {
        return partFromRoute;
    }

    return partStore.getPartOrUseDefault(props.currentSchool);
});

// Determine the course to redirect to based on the current route and the selected school
const newRouteCourse = (newSchool: FlightSchool): FlightSchoolCourse => {
    const coursesByPart = computed<{ [key in AvailableParts]: FlightSchoolCourse[] }>(() => (
        {
            141: newSchool.courses.filter((course) => course.certificate.part === 141),
            61: newSchool.courses.filter((course) => course.certificate.part === 61),
        }
    ));

    if (route.params.courseAcronym) {
        const searchAcronym = route.params.courseAcronym as string;

        let course: FlightSchoolCourse;

        // if the school has courses for the current part, use the course that matches the acronym or the first course
        if (currentPart.value && coursesByPart.value[currentPart.value].length > 0) {
            course = coursesByPart.value[currentPart.value].find(
                (c) => c.certificate.acronym === searchAcronym.toUpperCase(),
            ) || coursesByPart.value[currentPart.value][0];
        } else {
            // use the course that matches the acronym or the first course
            course = newSchool.courses.find(
                (c) => c.certificate.acronym === searchAcronym.toUpperCase(),
            ) || newSchool.courses[0];
        }

        return course;
    }

    /**
     * If there is no course acronym in the current route, use the first course for the current part or
     * the first course in the school if there are no courses for the current part
     */
    return (currentPart.value && coursesByPart.value[currentPart.value].length > 0)
        ? coursesByPart.value[currentPart.value][0]
        : newSchool.courses[0];
};

const schoolDefaultRoute = computed(
    () => ((route.meta.maintainRouteOnSchoolChange === false) ? 'classOverview' : route.name),
);

</script>

<template>
    <div class="row">
        <div class="column flight-school-label">
            <div
                v-if="currentSchool !== null"
                id="flight-school-name"
            >
                {{ currentSchool.name }}
            </div>
        </div>
    </div>
    <div
        v-if="schoolStore.schools.length > 1"
        id="fsum-switch-schools"
    >
        <FsumNavButton
            id="flight-school-menu-btn"
            role="menuitem"
            dense
            label="Switch Schools"
            icon="flight"
            @click="schoolDialogOpen = !schoolDialogOpen"
        />
        <q-dialog
            id="flight-school-menu"
            v-model="schoolDialogOpen"
        >
            <q-card style="max-width: 600px; width: 100%;">
                <q-card-section class="q-pb-none row justify-end">
                    <q-btn
                        v-close-popup
                        icon="close"
                        flat
                        round
                        dense
                    />
                </q-card-section>
                <q-card-section class="q-pb-none">
                    <q-input
                        v-model="schoolFilter"
                        class="school-search-bar"
                        outlined
                        label-slot
                        autofocus
                        dense
                    >
                        <template #label>
                            Search
                        </template>
                        <template #append>
                            <q-icon
                                name="search"
                                color="fsum-black"
                            />
                        </template>
                    </q-input>
                </q-card-section>
                <q-card-section>
                    <q-scroll-area style="height: 400px;">
                        <q-list
                            class="q-pr-md"
                            separator
                        >
                            <q-item
                                v-for="school in filteredSchools"
                                :key="school.id"
                                v-close-popup="true"
                                :clickable="true"
                                class="flight-school-option items-center"
                                :class="currentSchool?.id === school.id ? 'text-fsum-blue' : ''"
                                :to="{
                                    name: schoolDefaultRoute,
                                    params: {
                                        schoolSlug: school.urlSlug,
                                        part: newRouteCourse(school).certificate?.part,
                                        courseAcronym: newRouteCourse(school).certificate?.acronym.toLowerCase(),
                                        ratingAcronym: newRouteCourse(school).ratings[0]?.acronym.toLowerCase(),
                                    },
                                }"
                            >
                                {{ school.name }}
                            </q-item>
                        </q-list>
                    </q-scroll-area>
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<style scoped lang="scss">
#flight-school-name {
    font-size: 16px;
    color: var(--fsum-black);
}
.flight-school-option {
    font-size: 14px;
}
</style>
<style lang="scss">
.school-search-bar:not(.q-field--focused) {
    .q-placeholder + .q-field__label {
        color: var(--fsum-black);
    }
}
</style>
